import {
  FC,
  useCallback,
  useEffect,
  useState,
  lazy,
  Suspense,
  useMemo,
} from 'react';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import clsx from 'clsx';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { useProfileContext } from '@components/Contexts/ProfileContext';
import { useProfilePageBi } from '@components/Hooks/useProfilePageBi';
import ArrowSvg from '@components/Images/ArrowSvg';
import {
  CLIENT_PROJECTS_PORTFOLIO_INCREMENT,
  CLIENT_PROJECTS_PORTFOLIO_INITIAL_DISPLAY,
  CLIENT_PROJECTS_PORTFOLIO_MOBILE_INCREMENT,
  FEATURED_PROJECT_URL_PREFIX,
} from '@utils/constants';
import ProjectGrid from './ProjectGrid/ProjectGrid';
import { PortfolioItemWithUrl } from './Project/Project';
import ProjectErrorToast from './ProjectErrorToast';
import CollapseButton from '../CollapseButton';
import { PortfolioItem } from '@wix/ambassador-partners-profile-v1-partner-profile/build/cjs/types.impl';
import s from './ClientProjects.module.scss';

const ProjectModal = lazy(() => import('./ProjectModal'));

type Props = {
  isMobileView?: boolean;
  onShowLess?: () => void;
};

const ClientProjects: FC<Props> = ({ isMobileView, onShowLess }) => {
  const { t } = useTranslation();
  const { logProfilePageClick, logProjectModalClick, logProjectClick } =
    useProfilePageBi();
  const { partnerProfile, portfolioUrlMappings } = useProfileContext();
  const router = useRouter();
  useEffect(() => {
    setImageSlideWidth(
      isMobileView ? (window.screen.width * 93.6) / 100 + 'px' : 'auto',
    );
  }, [isMobileView]);

  const [quantityToDisplay, setQuantityToDisplay] = useState<number>(
    CLIENT_PROJECTS_PORTFOLIO_INITIAL_DISPLAY,
  );
  const [imageSlideWidth, setImageSlideWidth] = useState('auto');
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] =
    useState<PortfolioItemWithUrl | null>(null);
  const [showError, setShowError] = useState<boolean>(false);

  // Get portfolio items and enhance with URLs from pre-fetched mappings
  const portfolioItems = _.get(partnerProfile, 'portfolio.items', []);

  const allProjects: PortfolioItemWithUrl[] = useMemo(
    () =>
      portfolioItems.map((item: PortfolioItem) => ({
        ...item,
        url:
          item.metaSiteId &&
          portfolioUrlMappings[item.metaSiteId] &&
          portfolioUrlMappings[item.metaSiteId] !== null
            ? portfolioUrlMappings[item.metaSiteId]!
            : `${FEATURED_PROJECT_URL_PREFIX}/${item.metaSiteId}`,
      })),
    [portfolioItems, portfolioUrlMappings],
  );

  const maxLength = _.size(portfolioItems);
  const remainingLength = maxLength - quantityToDisplay;

  useEffect(() => {
    const { projectId } = router.query;
    if (projectId) {
      const project = allProjects.find((p) => p.metaSiteId === projectId);
      if (project) {
        setSelectedProject(project);
        setIsProjectModalOpen(true);
      }
    }
  }, [router.query, allProjects]);

  const clearError = useCallback(() => {
    setShowError(false);
  }, []);

  const handleProjectClick = useCallback((project: PortfolioItemWithUrl) => {
    if (!project.metaSiteId) {
      setShowError(true);
      return;
    }

    setSelectedProject(project);
    setIsProjectModalOpen(true);
    logProjectClick(
      'page view',
      JSON.stringify({ metaSiteId: project.metaSiteId }),
      'profile page',
    );

    const url = new URL(window.location.href);
    url.searchParams.set('projectId', project.metaSiteId);
    window.history.replaceState({}, '', url.toString());
  }, []);

  const closeProjectModal = useCallback((project: PortfolioItemWithUrl) => {
    setIsProjectModalOpen(false);
    setSelectedProject(null);
    logProjectModalClick(
      'close project',
      JSON.stringify({ metaSiteId: project.metaSiteId }),
    );

    const url = new URL(window.location.href);
    url.searchParams.delete('projectId');
    window.history.replaceState({}, '', url.toString());
  }, []);

  const logShowMoreClick = useCallback(() => {
    logProfilePageClick('show more results');
  }, []);

  const handleToggle = useCallback(
    (shouldShowMore: boolean) => {
      if (shouldShowMore) {
        // Show More
        const increment = Math.min(
          remainingLength,
          isMobileView
            ? CLIENT_PROJECTS_PORTFOLIO_MOBILE_INCREMENT
            : CLIENT_PROJECTS_PORTFOLIO_INCREMENT,
        );
        setQuantityToDisplay((prev) => prev + increment);
        logShowMoreClick();
      } else {
        // Show Less
        logProfilePageClick('show less results');
        setQuantityToDisplay(CLIENT_PROJECTS_PORTFOLIO_INITIAL_DISPLAY);
        onShowLess?.();
      }
    },
    [remainingLength, isMobileView, logShowMoreClick, onShowLess],
  );

  return (
    <>
      {showError && (
        <ProjectErrorToast isMobileView={isMobileView} onDismiss={clearError} />
      )}
      <Box
        direction="vertical"
        dataHook="client-projects"
        width={imageSlideWidth}
        className={clsx(s.projectsContainer, isMobileView && s.mobile)}
      >
        <ProjectGrid
          projects={allProjects}
          quantityToDisplay={quantityToDisplay}
          isMobileView={isMobileView}
          onProjectClick={handleProjectClick}
        />

        <CollapseButton
          totalCount={maxLength}
          visibleCount={quantityToDisplay}
          initialCount={CLIENT_PROJECTS_PORTFOLIO_INITIAL_DISPLAY}
          showMoreText={t('show-more-text-button')}
          showLessText={t('show-less-text-button')}
          onToggle={handleToggle}
          iconConfig={{
            element: <ArrowSvg />,
            baseClassName: s.showMoreLessButtonIcon,
            moreClassName: s.isMore,
            lessClassName: s.isLess,
          }}
        />

        {isProjectModalOpen && (
          <Suspense fallback={null}>
            <ProjectModal
              isOpen={isProjectModalOpen}
              closeModal={closeProjectModal}
              currentProject={selectedProject}
              isMobileView={!!isMobileView}
              projects={allProjects}
            />
          </Suspense>
        )}
      </Box>
    </>
  );
};

export default ClientProjects;
